import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import api, { formatApiError } from "../../lib/api";
import {
  Coins, RefreshCw, Play, PauseCircle, ShieldCheck, CheckCircle2, IndianRupee,
  RotateCcw, Filter, AlertTriangle,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";

/**
 * Admin console for the incentive payout ledger (Phase 4.5).
 *
 * Server returns a `capabilities` object per request so we hide any action
 * button the caller lacks permission for. The server still enforces the
 * gate — this is UX only.
 *
 * Also uses a request-id counter (see `reqIdRef`) so out-of-order responses
 * from rapid filter changes cannot overwrite fresh data with stale rows.
 */
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const currentPeriod = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const STATUSES = ["accrued", "verified", "approved", "paid", "hold"];
const STATUS_COLOR = {
  accrued: "bg-yellow-100 text-yellow-900",
  verified: "bg-blue-100 text-blue-900",
  approved: "bg-emerald-100 text-emerald-900",
  paid: "bg-green-800 text-white",
  hold: "bg-red-100 text-red-900",
};

export default function IncentivePayouts() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [caps, setCaps] = useState({});
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [periodState, setPeriodState] = useState(null);
  const [filters, setFilters] = useState({ period: currentPeriod(), status: "", user_id: "" });
  const [busyId, setBusyId] = useState("");
  const [users, setUsers] = useState([]);

  // Confirmation dialog state — one shared dialog for Recompute / Finalize.
  const [confirmState, setConfirmState] = useState({ open: false, kind: null });
  // Hold-reason dialog state — dedicated because it needs a text input.
  const [holdState, setHoldState] = useState({ open: false, payoutId: null, reason: "" });

  // Request-id counter — ensures only the *latest* fetch write to state.
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = {};
      if (filters.period) params.period = filters.period;
      if (filters.status) params.status = filters.status;
      if (filters.user_id) params.user_id = filters.user_id;
      const { data } = await api.get("/admin/incentive-payouts", { params });
      // Drop the response if a newer request has been kicked off already.
      if (myId !== reqIdRef.current) return;
      setAccessDenied(false);
      setRows(data.items || []);
      setTotals(data.totals || {});
      setCaps(data.capabilities || {});
      // Fetch the period bulletin state — non-blocking; ignore errors.
      const p = params.period || currentPeriod();
      api.get("/admin/incentive-payouts/period-state", { params: { period: p } })
        .then((r) => { if (myId === reqIdRef.current) setPeriodState(r.data); })
        .catch(() => { if (myId === reqIdRef.current) setPeriodState(null); });
    } catch (e) {
      if (myId !== reqIdRef.current) return;
      // 403 → explicit access-denied panel instead of misleading empty
      // state. Any other error still shows the toast.
      if (e?.response?.status === 403) {
        setAccessDenied(true);
        setRows([]);
        setTotals({});
        setCaps({});
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  }, [filters]);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/debt/team-users");
      setUsers(data.items || []);
    } catch (_e) { /* silent — filter still works with free text */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const act = async (id, action, extra = {}) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/incentive-payouts/${id}/${action}`, extra);
      toast.success(`Payout ${data.status}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(""); }
  };

  const runConfirmed = async () => {
    const kind = confirmState.kind;
    setConfirmState({ open: false, kind: null });
    if (kind === "recompute") {
      try {
        const { data } = await api.post("/admin/incentive-payouts/recompute", { months_back: 1 });
        toast.success(`Recompute: ${data.accrued} new payout(s) accrued`);
        await load();
      } catch (e) { toast.error(formatApiError(e)); }
    } else if (kind === "finalize") {
      try {
        const { data } = await api.post("/admin/incentive-payouts/finalize-month",
          { period: filters.period || currentPeriod() });
        toast.success(`Finalized: ${data.adjusted} adjusted · Δ ${inr(data.delta_inr)}`);
        await load();
      } catch (e) { toast.error(formatApiError(e)); }
    }
  };

  const submitHold = async () => {
    const reason = holdState.reason.trim();
    if (reason.length < 3) {
      toast.error("Hold reason must be at least 3 characters");
      return;
    }
    const id = holdState.payoutId;
    setHoldState({ open: false, payoutId: null, reason: "" });
    await act(id, "hold", { reason });
  };

  const grandTotal = useMemo(() => (totals?.all ?? 0), [totals]);

  return (
    <DashboardShell title="Incentive Payouts" subtitle="Admin · Ledger · Finance verify → Debt BU Head approve → Finance mark paid">
      <Toaster richColors position="top-right" />
      <div className="space-y-4" data-testid="admin-incentive-payouts">
        {accessDenied ? (
          <div
            data-testid="payouts-access-denied"
            className="border border-amber-300 bg-amber-50 rounded-lg p-8 text-center"
          >
            <AlertTriangle size={28} className="text-amber-600 mx-auto mb-3" />
            <h3 className="text-base font-heading text-navy mb-1">Access denied</h3>
            <p className="text-sm text-ink-muted max-w-md mx-auto">
              You need <span className="font-mono">targets_incentives · write</span> on the
              Roles CMS matrix to open the payout ledger. Your incentives are on the
              <a href="/my-incentives" className="text-navy underline ml-1">My Incentives</a> page.
            </p>
          </div>
        ) : (
          <>
        {/* Totals strip */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <TotalTile label="Grand total" value={inr(grandTotal)} accent />
          {STATUSES.map((s) => (
            <TotalTile key={s} label={s} value={inr(totals?.[s] || 0)} />
          ))}
        </div>

        {/* Finalize-month bulletin — shows only when the period has accrued
            rows AND finalize_month has not run yet. Non-blocking; the
            action buttons remain fully usable. */}
        {periodState?.needs_finalize && caps.can_finalize && (
          <div
            data-testid="payouts-finalize-bulletin"
            className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
          >
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-heading text-amber-900">
                Run <b>Finalize month</b> for {filters.period || currentPeriod()} before verifying rows
              </div>
              <div className="text-[11px] text-amber-800 mt-0.5">
                {periodState.accrued_count} accrued payout{periodState.accrued_count === 1 ? "" : "s"} in this period —
                team multipliers are only applied by the month-end sweep. Verifying now locks these rows
                at their base 1× amount.
              </div>
            </div>
            <button
              onClick={() => setConfirmState({ open: true, kind: "finalize" })}
              className="text-[11px] font-heading text-navy bg-yellow-400 hover:bg-yellow-500 rounded px-3 py-1.5 inline-flex items-center gap-1 shrink-0"
              data-testid="payouts-finalize-bulletin-action"
            >
              <ShieldCheck size={11} /> Finalize now
            </button>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 border border-ink/10 bg-white rounded-lg p-3">
          <Filter size={12} className="text-ink-muted" />
          <label className="text-[10px] text-ink-muted font-heading uppercase tracking-wider">Period</label>
          <input type="month" value={filters.period}
                  onChange={(e) => setFilters((f) => ({ ...f, period: e.target.value }))}
                  className="text-[12px] font-mono border border-ink/15 rounded px-2 py-1 bg-white"
                  data-testid="payouts-filter-period" />
          <label className="text-[10px] text-ink-muted font-heading uppercase tracking-wider ml-2">User</label>
          <select value={filters.user_id} onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))}
                  className="text-[12px] border border-ink/15 rounded px-2 py-1 bg-white max-w-[220px]"
                  data-testid="payouts-filter-user">
            <option value="">All</option>
            {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
          </select>
          <label className="text-[10px] text-ink-muted font-heading uppercase tracking-wider ml-2">Status</label>
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  className="text-[12px] border border-ink/15 rounded px-2 py-1 bg-white"
                  data-testid="payouts-filter-status">
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex-1" />
          <button onClick={load}
                    className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1"
                    data-testid="payouts-refresh">
            <RefreshCw size={11} /> Refresh
          </button>
          {caps.can_recompute && (
            <button onClick={() => setConfirmState({ open: true, kind: "recompute" })}
                      className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1"
                      data-testid="payouts-recompute">
              <Play size={11} /> Recompute
            </button>
          )}
          {caps.can_finalize && (
            <button onClick={() => setConfirmState({ open: true, kind: "finalize" })}
                      className="text-[11px] font-heading text-navy bg-yellow-400 hover:bg-yellow-500 rounded px-3 py-1 inline-flex items-center gap-1"
                      data-testid="payouts-finalize">
              <ShieldCheck size={11} /> Finalize month
            </button>
          )}
        </div>

        {/* Table */}
        <div className="border border-ink/10 rounded-lg bg-white overflow-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-navy/5 text-navy">
              <tr>
                <th className="text-left px-3 py-2 border-b border-ink/10">Period</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">Recipient</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">Rule</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">Event</th>
                <th className="text-right px-3 py-2 border-b border-ink/10">Amount</th>
                <th className="text-right px-3 py-2 border-b border-ink/10">Mult.</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">Status</th>
                <th className="text-right px-3 py-2 border-b border-ink/10">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="p-4 text-xs italic text-ink-muted">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="p-4 text-xs italic text-ink-muted">No payouts match these filters. Try Recompute or widen the period.</td></tr>
              )}
              {rows.map((r) => {
                const isBusy = busyId === r.payout_id;
                return (
                  <tr key={r.payout_id} className="border-b border-ink/5" data-testid={`payout-row-${r.payout_id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{r.period}</td>
                    <td className="px-3 py-2">
                      <div className="text-navy font-heading">{r.user_name || r.user_email || r.user_id}</div>
                      <div className="text-[10px] text-ink-muted font-mono">{r.user_role}</div>
                    </td>
                    <td className="px-3 py-2 text-[11px]">{r.rule_name}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-ink-muted">{r.trigger_event} · {r.event_ref?.split(":")[1]?.slice(0, 12) || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-navy">{inr(r.amount_inr)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[11px]">{r.team_multiplier_applied}×</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-heading px-1.5 py-0.5 rounded-full uppercase ${STATUS_COLOR[r.status] || "bg-ink/5"}`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {r.status === "accrued" && caps.can_verify && (
                        <button disabled={isBusy} onClick={() => act(r.payout_id, "verify")}
                                  className="text-[10px] font-heading text-navy border border-navy/30 hover:bg-navy/5 rounded px-2 py-1 disabled:opacity-40"
                                  data-testid={`payout-verify-${r.payout_id}`}>Verify</button>
                      )}
                      {r.status === "verified" && caps.can_approve && (
                        <button disabled={isBusy} onClick={() => act(r.payout_id, "approve")}
                                  className="text-[10px] font-heading text-navy border border-navy/30 hover:bg-navy/5 rounded px-2 py-1 disabled:opacity-40"
                                  data-testid={`payout-approve-${r.payout_id}`}>Approve</button>
                      )}
                      {r.status === "approved" && caps.can_mark_paid && (
                        <button disabled={isBusy} onClick={() => act(r.payout_id, "mark-paid")}
                                  className="text-[10px] font-heading text-navy bg-yellow-400 hover:bg-yellow-500 rounded px-2 py-1 inline-flex items-center gap-1 disabled:opacity-40"
                                  data-testid={`payout-pay-${r.payout_id}`}>
                          <IndianRupee size={10} /> Mark paid
                        </button>
                      )}
                      {r.status === "hold" && caps.can_unhold && (
                        <button disabled={isBusy} onClick={() => act(r.payout_id, "unhold")}
                                  className="text-[10px] font-heading text-emerald-700 border border-emerald-300 hover:bg-emerald-50 rounded px-2 py-1 inline-flex items-center gap-1 disabled:opacity-40"
                                  data-testid={`payout-unhold-${r.payout_id}`}>
                          <RotateCcw size={10} /> Unhold
                        </button>
                      )}
                      {r.status !== "paid" && r.status !== "hold" && caps.can_hold && (
                        <button disabled={isBusy}
                                  onClick={() => setHoldState({ open: true, payoutId: r.payout_id, reason: "" })}
                                  className="text-[10px] text-red-600 hover:bg-red-50 rounded px-2 py-1 ml-1 inline-flex items-center gap-1 disabled:opacity-40"
                                  data-testid={`payout-hold-${r.payout_id}`}>
                          <PauseCircle size={10} /> Hold
                        </button>
                      )}
                      {r.status === "paid" && (
                        <span className="text-[10px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={11} /> settled</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          </>
        )}
      </div>

      {/* Recompute / Finalize confirmation */}
      <AlertDialog open={confirmState.open} onOpenChange={(o) => !o && setConfirmState({ open: false, kind: null })}>
        <AlertDialogContent data-testid="payouts-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-600" />
              {confirmState.kind === "recompute" ? "Recompute payouts" : "Finalize month"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmState.kind === "recompute"
                ? "This walks every sanction / disbursement in the current + previous month and creates any missing ledger rows. Safe to re-run — idempotent."
                : `Apply team multipliers to every accrued row in ${filters.period || currentPeriod()}. This bumps payout amounts based on team achievement %.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="payouts-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirmed} data-testid="payouts-confirm-run">Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hold reason dialog */}
      <Dialog open={holdState.open} onOpenChange={(o) => !o && setHoldState({ open: false, payoutId: null, reason: "" })}>
        <DialogContent data-testid="payouts-hold-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PauseCircle size={16} className="text-red-600" />
              Place payout on hold
            </DialogTitle>
            <DialogDescription>
              Provide a reason (min 3 characters). This is logged in the payout&apos;s audit trail.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="payouts-hold-reason"
            value={holdState.reason}
            onChange={(e) => setHoldState((s) => ({ ...s, reason: e.target.value }))}
            placeholder="e.g. Pending clarification on team achievement %"
            className="min-h-[90px]"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHoldState({ open: false, payoutId: null, reason: "" })}
                     data-testid="payouts-hold-cancel">Cancel</Button>
            <Button onClick={submitHold} disabled={holdState.reason.trim().length < 3}
                     data-testid="payouts-hold-submit">Place on hold</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

function TotalTile({ label, value, accent }) {
  return (
    <div className={`rounded-lg border ${accent ? "border-navy bg-navy text-yellow-300" : "border-ink/10 bg-white"} p-3`}>
      <div className={`text-[10px] font-heading uppercase tracking-wider mb-1 ${accent ? "text-yellow-300/80" : "text-ink-muted"} flex items-center gap-1`}>
        <Coins size={10} /> {label}
      </div>
      <div className={`text-sm font-heading ${accent ? "text-yellow-300" : "text-navy"}`}>{value}</div>
    </div>
  );
}
